import React, { useEffect, useState } from "react";
import { Wallet, TrendingUp, AlertTriangle, RefreshCw } from "lucide-react";
import { apiFetch } from "@/lib/api";

/**
 * Phase 24e · Creator earnings compact widget.
 * Loads /marketplace/creator/me/earnings and shows balance + lifetime stats.
 * Used as an injection into CreatorDashboardV3 (max +2 lines in host file).
 */
export function CreatorEarningsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await apiFetch("/marketplace/creator/me/earnings");
      setData(res);
    } catch {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div
        data-testid="creator-earnings-widget-loading"
        className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 animate-pulse text-xs text-text-secondary"
      >
        Caricamento earnings…
      </div>
    );
  }
  if (!data || data.error) {
    return null; // silent when API disabled (503) — feature not visible to user
  }
  const eur = (c) => (Number(c || 0) / 100).toFixed(2).replace(".", ",");
  const balCents = Number(data.balance_cents || 0);
  const balColor = balCents > 0 ? "text-[var(--color-success)]" : "text-text-secondary";

  return (
    <div
      data-testid="creator-earnings-widget"
      className="rounded-2xl border border-[var(--color-border)] bg-gradient-to-br from-[var(--color-panel-ai)] to-[var(--color-card)] p-5 relative overflow-hidden"
    >
      <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-[var(--color-primary)]/10 blur-3xl pointer-events-none" />
      <div className="flex items-center justify-between mb-3 relative">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-[var(--color-secondary)]" />
          <h3 className="text-xs uppercase tracking-widest text-text-secondary font-semibold">
            Creator Earnings (V24e)
          </h3>
        </div>
        <button
          data-testid="creator-earnings-refresh"
          onClick={load}
          className="text-text-secondary hover:text-[var(--color-primary)] transition-colors"
        >
          <RefreshCw className="h-3 w-3" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3 relative">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-1">
            Balance
          </div>
          <div
            data-testid="earnings-balance"
            className={`text-2xl font-semibold tabular-nums ${balColor}`}
          >
            ${eur(data.balance_cents)}
          </div>
          {data.frozen && (
            <div className="flex items-center gap-1 mt-1 text-[10px] text-neutral-400">
              <AlertTriangle className="h-3 w-3" /> Frozen
            </div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-1">
            Sales
          </div>
          <div className="text-2xl font-semibold tabular-nums text-[var(--color-text-main)]">
            {data.sales_count || 0}
          </div>
          <div className="text-[10px] text-text-secondary mt-1">
            ${eur(data.sales_gross_cents)} gross
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-1">
            Paid Out
          </div>
          <div className="text-2xl font-semibold tabular-nums text-[var(--color-secondary)]">
            ${eur(data.lifetime_paid_out_cents)}
          </div>
          <div className="text-[10px] text-text-secondary mt-1 flex items-center gap-1">
            <TrendingUp className="h-3 w-3" /> lifetime
          </div>
        </div>
      </div>
      <div className="text-[10px] text-text-secondary mt-3 relative">
        Revenue split: 70% Creator / 30% Platform · Payout min $20 · Weekly cron Mon 06:00 UTC
      </div>
    </div>
  );
}

export default CreatorEarningsWidget;
